import type { ProblemDetails, Session } from "./types";

const API_ROOT = "/api/v1";
let accessToken = "";

export class ApiError extends Error {
  readonly status: number;
  readonly traceId?: string;
  readonly title?: string;

  constructor(status: number, problem: ProblemDetails) {
    super(problem.detail || problem.title || `Ошибка HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.traceId = problem.traceId;
    this.title = problem.title;
  }
}

export function setAccessToken(token: string): void {
  accessToken = token;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  headers.set("Accept", "application/json");

  const response = await fetch(`${API_ROOT}${path}`, { ...init, headers });
  if (response.status === 401 && accessToken) {
    accessToken = "";
    window.dispatchEvent(new Event("wms:unauthorized"));
  }
  if (response.status === 204) return undefined as T;
  if (!response.ok) {
    let problem: ProblemDetails = { status: response.status };
    try { problem = await response.json() as ProblemDetails; } catch { /* invalid server response */ }
    throw new ApiError(response.status, problem);
  }
  return await response.json() as T;
}

type OrderQuery = { status?: string; search: string; sort: string; descending: boolean; page: number };
function orderQuery(value: OrderQuery) {
  const query = new URLSearchParams({ search: value.search, sort: value.sort, descending: String(value.descending), page: String(value.page), pageSize: "50" });
  if (value.status) query.set("status", value.status);
  return query.toString();
}
interface PagedItems<T> { items: T[]; total: number; page: number; pageSize: number }

export const api = {
  refreshSession: () => request<{ accessToken: string; expiresAt: string }>("/auth/refresh", { method: "POST" }),
  oneCInboundList: (kind: string, historical: boolean, page: number, search = "", state = "", warehouse = "", placement = false) => request<unknown>(`/one-c-inbound?${new URLSearchParams({ kind, historical: String(historical), page: String(page), search, state, ...(warehouse ? { warehouse } : {}), placement: String(placement) })}`),
  oneCInboundDetail: (id: string) => request<unknown>(`/one-c-inbound/${id}`),
  oneCInboundApply: (id: string, body: unknown) => request<unknown>(`/one-c-inbound/${id}/operations`, { method: "POST", body: JSON.stringify(body) }),
  localOneCStockComparison: () => request<import("./types").OneCStockComparison[]>("/one-c-local/stock-comparison"),
  localOneCSourceOrders: (disposition: string, page: number) => request<{ total: number; items: import("./types").OneCSourceOrder[] }>(`/one-c-local/source-orders?disposition=${encodeURIComponent(disposition)}&page=${page}`),
  localOneCStatus: () => request<import("./types").OneCLocalStatus>("/one-c-local/status"),
  localOneCSync: () => request<import("./types").OneCLocalStatus>("/one-c-local/sync", { method: "POST" }),
  localOneCFullSync: () => request<import("./types").OneCLocalStatus>("/one-c-local/full-sync", { method: "POST" }),
  localOneCRefresh: (id: string) => request<void>(`/one-c-local/orders/${id}/refresh`, { method: "POST" }),
  localOneCFinance: (id: string, action: "invoice" | "pay" | "pay-half" | "credit" | "revoke-credit") => request<void>(`/one-c-local/orders/${id}/finance/${action}`, { method: "POST" }),
  localOneCTest: (action: "create-edge-orders" | "arm-ack-failure" | "resume-events") => request<import("./types").OneCLocalStatus>(`/one-c-local/test/${action}`, { method: "POST" }),
  localOneCOrderTest: (id: string, action: "change" | "cancel") => request<void>(`/one-c-local/orders/${id}/test/${action}`, { method: "POST" }),
  login: (email: string, password: string) => request<{ accessToken: string; expiresAt: string }>("/auth/token", {
    method: "POST", body: JSON.stringify({ email, password })
  }),
  users: () => request<import("./types").User[]>("/auth/users"),
  createUser: (payload: { email: string; displayName: string; password: string; role: string }) => request<import("./types").User>("/auth/users", {
    method: "POST", body: JSON.stringify(payload)
  }),
  warehouses: () => request<import("./types").Warehouse[]>("/warehouses"),
  locations: (warehouseId: string) => request<import("./types").Location[]>(`/warehouses/${warehouseId}/locations`),
  createLocation: (warehouseId: string, payload: Record<string, string>) => request<{ id: string }>(`/warehouses/${warehouseId}/locations`, {
    method: "POST", body: JSON.stringify(payload)
  }),
  balances: (warehouseId?: string, productCode?: string) => {
    const query = new URLSearchParams();
    if (warehouseId) query.set("warehouseId", warehouseId);
    if (productCode) query.set("productCode", productCode);
    return request<import("./types").Balance[]>(`/inventory/balances?${query}`);
  },
  adjustInventory: (payload: { locationBarcode: string; productCode: string; quantityDelta: number; documentReference: string }) => request("/inventory/adjustments", {
    method: "POST", body: JSON.stringify(payload)
  }),
  ordersPage: (value: OrderQuery) => request<PagedItems<import("./types").OrderSummary>>(`/orders/page?${orderQuery(value)}`),
  partnerFulfillmentPage: (value: OrderQuery) => request<PagedItems<import("./types").PartnerFulfillmentSummary>>(`/partner-fulfillment/page?${orderQuery(value)}`),
  orderOverview: () => request<{new: number;active: number;ready: number;issued: number;items: import("./types").OrderSummary[]}>("/orders/overview"),
  orders: (status?: string) => request<import("./types").OrderSummary[]>(`/orders${status ? `?status=${status}` : ""}`),
  order: (id: string) => request<import("./types").OrderDetail>(`/orders/${id}`),
  orderReview: (id: string) => request<import("./types").OrderReviewPlan>(`/orders/${id}/review`),
  resolveOrderReview: (id: string, payload: { operationId: string; messageId: string; action: "rebuild" | "cancel"; stagingBarcode: string; returns: import("./types").ReviewReturn[] }) => request<{ alreadyProcessed: boolean; newTaskId?: string }>(`/orders/${id}/review`, { method: "POST", body: JSON.stringify(payload) }),
  partnerFulfillment: () => request<import("./types").PartnerFulfillmentSummary[]>("/partner-fulfillment/"),
  partnerFulfillmentDetail: (id: string) => request<import("./types").PartnerFulfillmentDetail>(`/partner-fulfillment/${id}`),
  assignOrder: (id: string, assignedUserId: string, stagingLocationId: string) => request<{ id: string; status: string }>(`/orders/${id}/assign`, {
    method: "POST", body: JSON.stringify({ assignedUserId, stagingLocationId })
  }),
  issueOrder: (id: string) => request<void>(`/orders/${id}/issue`, { method: "POST" }),
  myTasks: () => request<import("./types").PickingTask[]>("/picking-tasks/mine"),
  scanPickStatus: (taskId: string, payload: { operationId: string; locationBarcode: string; productBarcode: string; quantity: number }) =>
    request<{ alreadyProcessed: boolean }>(`/picking-tasks/${taskId}/scan-status?${new URLSearchParams({ ...payload, quantity: String(payload.quantity) })}`),
  scanPick: (taskId: string, payload: { operationId: string; locationBarcode: string; productBarcode: string; quantity: number }) => request<{ alreadyProcessed: boolean; taskId: string; status?: string }>(`/picking-tasks/${taskId}/scan`, {
    method: "POST", body: JSON.stringify(payload)
  }),
  analyticsAbcXyz: (startDate?: string, endDate?: string) => {
    const query = new URLSearchParams();
    if (startDate) query.set("startDate", startDate);
    if (endDate) query.set("endDate", endDate);
    return request<import("./types").AbcXyzReportResponse>(`/analytics/abc-xyz?${query}`);
  },
  analyticsSourceStatus: () => request<{ enabled: boolean; historyReady: boolean; syncedAt: string | null;
    fullSyncedAt: string | null; checkedAt: string | null; error: string; documents: number;
    expensesReady: boolean; expensesSyncedAt: string | null; expensesError: string }>("/analytics/source-status"),
  analyticsProfit: (startDate?: string, endDate?: string) => {
    const query = new URLSearchParams();
    if (startDate) query.set("startDate", startDate);
    if (endDate) query.set("endDate", endDate);
    return request<import("./types").ProfitAnalyticsResponse>(`/analytics/profit?${query}`);
  },
  analyticsCustomers: (startDate?: string, endDate?: string) => {
    const query = new URLSearchParams();
    if (startDate) query.set("startDate", startDate);
    if (endDate) query.set("endDate", endDate);
    return request<import("./types").CustomerAnalyticsResponse>(`/analytics/customers?${query}`);
  },
  analyticsCustomerProducts: (customerId: string, startDate?: string, endDate?: string) => {
    const query = new URLSearchParams();
    if (startDate) query.set("startDate", startDate);
    if (endDate) query.set("endDate", endDate);
    return request<import("./types").CustomerProductsAnalyticsResponse>(`/analytics/customers/${customerId}/products?${query}`);
  },
  analyticsMarginAlerts: (startDate?: string, endDate?: string) => {
    const query = new URLSearchParams();
    if (startDate) query.set("startDate", startDate);
    if (endDate) query.set("endDate", endDate);
    return request<import("./types").MarginAlertsResponse>(`/analytics/margin-alerts?${query}`);
  },
  analyticsInventoryAssets: () => request<import("./types").InventoryAssetsResponse>("/analytics/inventory-assets"),
  analyticsExpenses: (startDate?: string, endDate?: string) => {
    const query = new URLSearchParams();
    if (startDate) query.set("startDate", startDate);
    if (endDate) query.set("endDate", endDate);
    return request<import("./types").ExpenseAnalyticsResponse>(`/analytics/expenses?${query}`);
  },
  catalogProducts: () => request<import("./types").ProductCatalogItem[]>("/catalog/products"),
  catalogCategories: () => request<import("./types").ProductCategoryItem[]>("/catalog/categories"),
  createCatalogCategory: (data: { name: string; isActive: boolean }) => request<{ id: string }>("/catalog/categories", { method: "POST", body: JSON.stringify(data) }),
  updateCatalogCategory: (id: string, data: { name: string; isActive: boolean }) => request<void>(`/catalog/categories/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  uploadCatalogCategoryImage: (id: string, file: File) => { const body = new FormData(); body.append("file", file); return request<void>(`/catalog/categories/${id}/image`, { method: "POST", body }); },
  updateCatalogProduct: (id: string, data: { name: string; salePrice: number; isStorefrontPublished: boolean; categoryId: string | null; brand: string; description: string; specifications: Record<string,string> }) => request<void>(`/catalog/products/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  uploadCatalogImage: (id: string, file: File, altText: string) => { const body = new FormData(); body.append("file", file); body.append("altText", altText); return request<any>(`/catalog/products/${id}/images`, { method: "POST", body }); },
  deleteCatalogImage: (productId: string, imageId: string) => request<void>(`/catalog/products/${productId}/images/${imageId}`, { method: "DELETE" }),
  partnerCatalog: () => request<Array<{ id: string; code: string; name: string; unit: string; salePrice: number; partnerPrice: number | null; isPartnerVisible: boolean }>>("/partner-catalog/"),
  updatePartnerCatalog: (id: string, data: { isPartnerVisible: boolean; partnerPrice: number | null }) => request<void>(`/partner-catalog/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  edoPing: () => request<{ connected: boolean }>("/integration/edo/ping"),
  edoStatus: () => request<import("./types").EdoStatus>("/integration/edo/status"),
  edoReviewInbox: () => request<Array<{id:string;documentNumber:string;messageType:string;documentDate:string|null;status:string;error:string;processingAttempts:number}>>("/integration/edo/inbox?status=Deferred&limit=500"),
  edoInboxSource: (id: string) => request<{id:string;rawJson:string}>(`/integration/edo/inbox/${id}`),
  edoStockReceipts: (pendingOnly = false) => request<import("./types").EdoStockReceipt[]>(`/integration/edo/stock-receipts?pendingOnly=${pendingOnly}`),
  edoTradeDocuments: () => request<import("./types").EdoTradeDocument[]>("/integration/edo/trade-documents"),
  placeEdoStockReceipt: (lineId: string, value: {operationId:string;locationId:string;quantity:number;confirmPhysicalReceipt:boolean}) =>
    request<{placementId:string;alreadyApplied:boolean;remainingQuantity:number;cellQuantity:number}>(`/integration/edo/stock-receipts/lines/${lineId}/place`, {method:"POST",body:JSON.stringify(value)}),
  edoSyncAll: () => request<import("./types").EdoSyncResult[]>("/integration/edo/sync/all", { method: "POST" }),
  edoSyncOrders: () => request<import("./types").EdoSyncResult>("/integration/edo/sync/orders", { method: "POST" }),
  edoSyncMessages: () => request<import("./types").EdoSyncResult>("/integration/edo/sync/messages", { method: "POST" }),
  edoDispatchOutbox: () => request<import("./types").EdoOutboxDispatchResult>("/integration/edo/sync/outbox", { method: "POST" }),
  inventoryMoveStatus: (operationId: string) => request<{ operationId: string; alreadyProcessed: boolean }>(`/inventory/moves/${operationId}`),
  inventoryMove: (data: import("./types").MoveInventoryRequest) => request<{ operationId: string; alreadyProcessed: boolean }>("/inventory/move", { method: "POST", body: JSON.stringify(data) })
};

export function decodeSession(token: string, expiresAt: string): Session {
  const part = token.split(".")[1] ?? "";
  const normalized = part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=");
  const bytes = Uint8Array.from(atob(normalized), character => character.charCodeAt(0));
  const payload = JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
  const rawRoles = payload.role;
  const roles = (Array.isArray(rawRoles) ? rawRoles : [rawRoles]).filter(Boolean) as Session["roles"];
  return {
    token,
    expiresAt,
    name: String(payload.name ?? "Сотрудник"),
    email: String(payload.email ?? ""),
    roles
  };
}

